import { createApi } from "@reduxjs/toolkit/query/react";
import { baseAuthQuery } from "../base/baseApi";
import { ApiResponse } from "../base/apiResponse";
import {
  EnrollStudentResponse,
  AdminAppStudentPaymentsResponse,
  EnrollmentAdditionalInformationResponse,
  EnrollmentApplicationResponse,
  FilterDto,
  HealthInsuranceStatusResponse,
  PreferredForeignLanguageResponse,
  StudentInfoAdminRequest,
  StudentInfoAdminResponse,
  StudentResponse,
  HealthInsuranceStatusRequest,
  PreferredForeignLanguageRequest,
  EnrolledStudentsReportResponse,
  StudentPreviousDiplomaRequest,
  NacidBasicInfo,
  StudentNacidEventResponse,
  StudentGradeReportResponse,
  StudentCountsGroupedResponse,
  NotTakenExamsReportResponse,
  StudentSemesterAttestationResponse,
  StudentSemesterAttestationRequest,
  StudentHistoryDetailsResponse,
  StudentSelectedSemestersAverageRequest,
  StudentSelectedSemestersAverageResponse,
} from "../../models/dtos";

export const studentsApi = createApi({
  reducerPath: "studentsApi",
  baseQuery: baseAuthQuery("students"),
  tagTypes: ["Student", "StudentPayments"],
  endpoints: (builder) => ({
    getStudents: builder.query<ApiResponse<StudentResponse[]>, Partial<FilterDto>>({
      query: (query) => {
        return {
          url: "paginated",
          method: "POST",
          body: query,
        };
      },
      providesTags: (_result, _error, filter) => {
        if (filter) {
          return [{ type: "Student", id: JSON.stringify(filter) }];
        }
        return ["Student"];
      },
    }),
    getStudent: builder.query<ApiResponse<StudentInfoAdminResponse>, number>({
      queryFn: async (studentId, _queryApi, _extraOptions, baseQuery) => {
        if (Number.isNaN(studentId)) {
          return {
            data: { data: {} } as ApiResponse<StudentInfoAdminResponse>,
            error: undefined,
          };
        }
        const response = await baseQuery({
          url: `adminapp/${studentId}`,
          method: "GET",
        });
        if (response.error) {
          return { error: response.error };
        }
        return { data: response.data as ApiResponse<StudentInfoAdminResponse> };
      },
      providesTags: (_result, _error, studentId) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    createStudent: builder.mutation<
      ApiResponse<EnrollStudentResponse>,
      { studentData: Partial<StudentInfoAdminRequest>; passport?: File | null }
    >({
      query: ({ studentData, passport }) => {
        const formData = new FormData();

        Object.entries(studentData).forEach(([key, value]) => {
          if (key === "picture" || key === "studentId") {
            return;
          }

          if (value === null || value === undefined) {
            return;
          }

          if (value instanceof Date) {
            formData.append(key, value.toISOString());
          } else if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        });

        if (passport) {
          console.log("Appending passport file:", passport);
          formData.append("passport", passport);
        } else {
          console.log("No passport file provided");
        }

        console.log("FormData entries:");
        for (const pair of formData.entries()) {
          console.log(pair[0], pair[1]);
        }

        return {
          url: "adminapp/create",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Student"],
    }),
    updateStudent: builder.mutation<ApiResponse<boolean | EnrollStudentResponse>, Partial<StudentInfoAdminResponse>>({
      query: (body) => {
        body.picture = undefined;
        return {
          url: `adminapp/update/${body.studentId}`,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    updateStudentHealth: builder.mutation<
      ApiResponse<boolean>,
      { studentId: number; request: Partial<HealthInsuranceStatusRequest> }
    >({
      query: ({ studentId, request }) => {
        return {
          url: `adminapp/${studentId}/updateHealthInsuranceStatus`,
          method: "POST",
          body: request,
        };
      },
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    updateStudentLang: builder.mutation<
      ApiResponse<boolean>,
      { studentId: number; request: Partial<PreferredForeignLanguageRequest> }
    >({
      query: ({ studentId, request }) => {
        return {
          url: `adminapp/${studentId}/updatePreferredForeignLanguage`,
          method: "POST",
          body: request,
        };
      },
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    updateEducation: builder.mutation<
      ApiResponse<boolean>,
      { studentId: number; request: Partial<StudentPreviousDiplomaRequest> }
    >({
      query: ({ studentId, request }) => {
        return {
          url: `adminapp/updatePreviousDiploma/${studentId}`,
          method: "POST",
          body: request,
        };
      },
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    uploadStudentPicture: builder.mutation<ApiResponse<boolean>, { studentId: number; picture: File }>({
      query: ({ studentId, picture }) => {
        console.log("studentId", studentId);
        console.log("picture", picture);
        const formData = new FormData();
        formData.append("picture", picture);
        return {
          url: `adminapp/picture/${studentId}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    uploadPassport: builder.mutation<ApiResponse<boolean>, { studentId: number; passport: File }>({
      query: ({ studentId, passport }) => {
        const formData = new FormData();
        formData.append("passport", passport);
        return {
          url: `adminapp/passport/${studentId}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    downloadPassport: builder.query<Blob, number>({
      query: (studentId) => ({
        url: `adminapp/passport/${studentId}`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    uploadRelocationFile: builder.mutation<ApiResponse<boolean>, { studentId: number; relocationFile: File }>({
      query: ({ studentId, relocationFile }) => {
        const formData = new FormData();
        formData.append("relocationFile", relocationFile);
        return {
          url: `adminapp/relocation/${studentId}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    downloadRelocationFile: builder.query<Blob, number>({
      query: (studentId) => ({
        url: `adminapp/relocation/${studentId}`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    uploadDiploma: builder.mutation<ApiResponse<boolean>, { studentId: number; diploma: File }>({
      query: ({ studentId, diploma }) => {
        const formData = new FormData();
        formData.append("diploma", diploma);
        return {
          url: `adminapp/diploma/${studentId}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    downloadDiploma: builder.query<Blob, number>({
      query: (studentId) => ({
        url: `adminapp/diploma/${studentId}`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    getEnrollmentApplication: builder.mutation<
      ApiResponse<EnrollmentApplicationResponse>,
      { degreeId: number; applicationNumber: string }
    >({
      query: (query) => {
        return {
          url: `adminapp/enrollment/getApplication`,
          method: "GET",
          params: query,
        };
      },
    }),
    getStudentPayments: builder.query<ApiResponse<AdminAppStudentPaymentsResponse[]>, number>({
      query: (studentId) => {
        return {
          url: `adminapp/student-payments/${studentId}`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, studentId) => {
        return [{ type: "StudentPayments", id: studentId }];
      },
    }),
    getStudentsBySpecialityId: builder.query<ApiResponse<StudentResponse[]>, number>({
      query: (specialityId) => ({
        url: `getStudentsBySpeciality/${specialityId}`,
      }),
      providesTags: ["Student"],
    }),
    getHealthInsuranceStatus: builder.query<ApiResponse<HealthInsuranceStatusResponse>, number>({
      query: (studentId) => {
        return {
          url: `adminapp/${studentId}/getHealthInsuranceStatus`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, studentId) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    getPreferredLanguage: builder.query<ApiResponse<PreferredForeignLanguageResponse>, number>({
      query: (studentId) => {
        return {
          url: `adminapp/${studentId}/getPreferredForeignLanguage`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, studentId) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    getAdditionalInfo: builder.query<ApiResponse<EnrollmentAdditionalInformationResponse>, number>({
      query: (studentId) => {
        return {
          url: `adminapp/${studentId}/additionalInfo`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, studentId) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    enroll: builder.mutation<
      ApiResponse<EnrollStudentResponse>,
      { applicationId: number; rankingApplicationId: number; specialityId: number; selectedSpecialityId: number | null }
    >({
      query: (body) => {
        return {
          url: `${body.applicationId}/enroll/${body.rankingApplicationId}/${body.specialityId}/${body.selectedSpecialityId}`,
          method: "POST",
        };
      },
    }),
    nextSemester: builder.mutation<ApiResponse<boolean>, { studentId: number }>({
      query: (body) => ({
        url: `nextSemester/${body.studentId}`,
        method: "POST",
        body: body,
      }),
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    downloadDormitoryDeclaration: builder.query<Blob, { admissionApplicationId: number | null }>({
      query: (params) => ({
        url: `admission/${params.admissionApplicationId}/dormitoryDeclaration`,
        method: "POST",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    downloadStudentDocumentProfile: builder.query<Blob, { admissionApplicationId: number | null }>({
      query: (params) => ({
        url: `admission/${params.admissionApplicationId}/studentDocumentProfile`,
        method: "POST",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    generateStudentEmail: builder.mutation<ApiResponse<boolean>, number>({
      query: (studentId) => {
        return {
          url: `generateStudentEmail/${studentId}`,
          method: "POST",
        };
      },
      invalidatesTags: (_result, _error, studentId) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    sendInfoEmail: builder.mutation<ApiResponse<boolean>, number>({
      query: (studentId) => {
        return {
          url: `sendInfoEmail/${studentId}`,
          method: "POST",
        };
      },
    }),
    unenrollStudent: builder.mutation<ApiResponse<boolean>, number>({
      query: (studentId) => {
        return {
          url: `unenrollStudent/${studentId}`,
          method: "POST",
        };
      },
      invalidatesTags: (_result, _error, studentId) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    sendDataToNacid: builder.mutation<ApiResponse<boolean>, number>({
      query: (studentId) => {
        return {
          url: `sendDataToNacid/${studentId}`,
          method: "POST",
        };
      },
      invalidatesTags: (_result, _error, studentId) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    multiSendDataToNacid: builder.mutation<ApiResponse<boolean>, number[]>({
      query: (studentIds) => {
        return {
          url: `multiSendDataToNacid`,
          method: "POST",
          body: studentIds,
        };
      },
      invalidatesTags: (_result, _error, studentIds) => {
        return studentIds.map((id) => ({ type: "Student", id }));
      },
    }),
    multiUpdateEmail: builder.mutation<ApiResponse<boolean>, number[]>({
      query: (studentIds) => {
        return {
          url: `multiUpdateEmail`,
          method: "POST",
          body: studentIds,
        };
      },
      invalidatesTags: (_result, _error, studentIds) => {
        return studentIds.map((id) => ({ type: "Student", id }));
      },
    }),
    fetchUanFromNacid: builder.query<ApiResponse<NacidBasicInfo>, number>({
      query: (studentId) => {
        return {
          url: `fetchUanFromNacid/${studentId}`,
          method: "GET",
        };
      },
    }),
    getStudentNacidEvents: builder.query<ApiResponse<StudentNacidEventResponse[]>, number>({
      query: (studentId) => {
        return {
          url: `${studentId}/nacidEvents`,
          method: "GET",
        };
      },
      transformResponse: (response: ApiResponse<StudentNacidEventResponse[]>) => {
        response.data.forEach((event) => {
          if (event.request) {
            event.requestObject = JSON.parse(event.request);
            if (event.requestObject.PassportCopy) {
              event.requestObject.PassportCopy = undefined;
            }
          }
          if (event.response) {
            event.responseObject = JSON.parse(event.response);
          }
        });
        return response;
      },
      providesTags: (_result, _error, studentId) => {
        return [{ type: "Student", id: studentId }];
      },
    }),
    getStudentHistoryDetails: builder.query<ApiResponse<StudentHistoryDetailsResponse>, number>({
      query: (studentHistoryId) => ({
        url: `adminapp/studentHistory/${studentHistoryId}/details`,
        method: "GET",
      }),
    }),
    getEnrolledStudentsReport: builder.query<ApiResponse<EnrolledStudentsReportResponse[]>, number>({
      query: (academicYearId) => {
        return {
          url: `reports/getEnrolledStudentsReport/${academicYearId}`,
          method: "GET",
        };
      },
    }),
    export: builder.query<Blob, Partial<FilterDto>>({
      query: (params) => ({
        url: `export`,
        method: "POST",
        body: params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    exportForMoodle: builder.query<Blob, Partial<FilterDto>>({
      query: (params) => ({
        url: `export-moodle`,
        method: "POST",
        body: params,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),

    getStudentGradesReport: builder.query<ApiResponse<StudentGradeReportResponse>, number>({
      query: (studentId) => {
        return {
          url: `reports/getStudentGradesStatus/${studentId}`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, studentId) => {
        return [{ type: "Student", id: studentId }];
      },
    }),

    getStudentSelectedSemestersAverage: builder.query<
      ApiResponse<StudentSelectedSemestersAverageResponse>,
      StudentSelectedSemestersAverageRequest
    >({
      query: (request) => ({
        url: "reports/getStudentSelectedSemestersAverage",
        method: "POST",
        body: request,
      }),
      providesTags: (_result, _error, request) => [{ type: "Student", id: request.studentId }],
    }),

    getNotTakenExamsReport: builder.query<
      ApiResponse<NotTakenExamsReportResponse[]>,
      { facultyId: number; year: number }
    >({
      query: ({ facultyId, year }) => {
        return {
          url: `reports/notTakenExams/${facultyId}/${year}`,
          method: "GET",
        };
      },
    }),

    getStudentCountsGrouped: builder.query<ApiResponse<StudentCountsGroupedResponse[]>, number>({
      query: (facultyId) => {
        return {
          url: `reports/studentCountsGrouped/${facultyId}`,
          method: "GET",
        };
      },
    }),

    getStudentSemesterAttestations: builder.query<ApiResponse<StudentSemesterAttestationResponse[]>, number>({
      query: (studentId) => ({
        url: `adminapp/getStudentSemesterAttestations?studentId=${studentId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, studentId) => [{ type: "Student", id: studentId }],
    }),

    updateStudentSemesterAttestation: builder.mutation<ApiResponse<boolean>, StudentSemesterAttestationRequest>({
      query: (request) => ({
        url: "adminapp/updateStudentSemesterAttestation",
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [{ type: "Student", id: request.studentId }],
    }),

    addStudentSemesterAttestation: builder.mutation<ApiResponse<boolean>, StudentSemesterAttestationRequest>({
      query: (body) => ({
        url: "adminapp/addStudentSemesterAttestation",
        method: "POST",
        body: body,
      }),
      invalidatesTags: (_result, _error, { studentId }) => {
        return [{ type: "Student", id: studentId }];
      },
    }),

    deleteStudentSemesterAttestation: builder.mutation<ApiResponse<boolean>, number>({
      query: (attestationId) => ({
        url: `adminapp/deleteStudentSemesterAttestation/${attestationId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, _attestationId) => [{ type: "Student", id: "LIST" }],
    }),

    sendDiplomaDataToNacid: builder.mutation<ApiResponse<boolean>, number>({
      query: (studentId) => ({
        url: `sendDiplomaDataToNacid/${studentId}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, studentId) => [{ type: "Student", id: studentId }],
    }),
  }),
});
